
import Vue from 'vue';
import _trim from 'lodash/trim';
import _uniqBy from 'lodash/uniqBy';
import _cloneDeep from 'lodash/cloneDeep';
import EmptyState from '@/components/states/EmptyState.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { CompanyCustomFieldDef } from '@run-diffusion/shared';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';

export default Vue.extend({
	name: 'EditCompanyCustomFieldDefs',
	props: {
		value: {
			type: Array as () => CompanyCustomFieldDef[],
			default: () => [],
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		const SHORT_TEXT_LENGTH: number = 50;
        const WINDOW_STEPS = {
			LIST: 'list',
			EDIT: 'edit',
		};
		return {
			MAX_NUM_CUSTOM_FIELDS: 5,
			WINDOW_STEPS,
			windowStep: WINDOW_STEPS.LIST,
			formValid: true,
			SHORT_TEXT_LENGTH,
			DEFAULT_TEXT_MAX_LENGTH,
			rules: {
				key: [
					getTextMaxLengthRule(SHORT_TEXT_LENGTH),
					v => !!_trim(v) || 'Required',
					v => /^[a-zA-Z]/.test(v) || 'Must start with a letter',
					v => /[a-zA-Z0-9]$/.test(v) || 'Must end with a letter or number',
					v => /^[a-zA-Z0-9_]+$/.test(v) || 'Can only contain letters, numbers, and underscores',
					v => !/__{1,}/.test(v) || 'Cannot contain consecutive underscores',
				],
				label: [
					getTextMaxLengthRule(DEFAULT_TEXT_MAX_LENGTH),
					v => !!_trim(v) || 'Required',
				],
			},
			localCustomFieldDefs: [] as CompanyCustomFieldDef[],
			editingField: null as CompanyCustomFieldDef | null,
			editingIndex: -1,
		};
	},
	watch: {
		value: {
			immediate: true,
			handler(newVal: CompanyCustomFieldDef[], oldVal: CompanyCustomFieldDef[]) {
				if (newVal !== oldVal) {
					this.localCustomFieldDefs = _cloneDeep(newVal || []);
				}
			},
		},
	},
	methods: {
		getShowTextCounterAt,
		formatAndSetKey(val: string) {
			if (!this.editingField) return;

			// Convert to lowercase and replace spaces/invalid chars with underscores
			let formatted: string = (val || '').toLowerCase()
				.replace(/[^a-z0-9]/g, '_'); // Replace all non-alphanumeric chars with underscores

			this.editingField = {
				...this.editingField,
				key: formatted,
			};
		},
		moveField(index: number, direction: number) {
			const newIndex: number = index + direction;
			if (newIndex < 0 || newIndex >= this.localCustomFieldDefs.length) return;

			const temp: CompanyCustomFieldDef = this.localCustomFieldDefs[index];
			this.localCustomFieldDefs.splice(index, 1);
			this.localCustomFieldDefs.splice(newIndex, 0, temp);
            this.emitInput();
		},
		startAddField() {
			if (this.localCustomFieldDefs.length >= this.MAX_NUM_CUSTOM_FIELDS) return;

			this.editingField = {
				key: '',
				label: '',
				type: 'STR',
			};
			this.editingIndex = -1;
			this.windowStep = this.WINDOW_STEPS.EDIT;
		},
		startEditField(field: CompanyCustomFieldDef) {
			this.editingField = _cloneDeep(field);
			this.editingIndex = this.localCustomFieldDefs.findIndex((f: CompanyCustomFieldDef) => f.key === field.key);
			this.windowStep = this.WINDOW_STEPS.EDIT;
		},
		removeField(index: number) {
			this.localCustomFieldDefs.splice(index, 1);
            this.emitInput();
		},
		onCancel() {
			this.editingField = null;
			this.editingIndex = -1;
			this.windowStep = this.WINDOW_STEPS.LIST;
		},
		setField() {
			if (!this.$refs.form.validate() || !this.editingField) return;

			if (this.editingIndex === -1) {
				// Adding new field
				this.localCustomFieldDefs.push(this.editingField);
			} else {
				// Editing existing field
				this.localCustomFieldDefs.splice(this.editingIndex, 1, this.editingField);
			}
            this.emitInput();
			this.onCancel();
		},
        emitInput() {
            // Clean up the values
            this.localCustomFieldDefs = this.localCustomFieldDefs.map((def: CompanyCustomFieldDef) => {
                return {
                    ...def,
                    key: _trim(def.key).toLowerCase(),
                    label: _trim(def.label),
                };
            });
            this.localCustomFieldDefs = this.localCustomFieldDefs.filter((def: CompanyCustomFieldDef) => {
                return def.key && def.label;
            });

            // Filter out duplicates, keeping only the last occurrence (hence the two reverses)
            this.localCustomFieldDefs = _uniqBy(this.localCustomFieldDefs.reverse(), 'key').reverse();

            // Emit cleaned up list
            this.$emit('input', this.localCustomFieldDefs);
        },
	},
	components: {
		EmptyState,
		BaseButton,
	},
});
